import { useAuth } from '../context/AuthContext';

/**
 * Hook personalizado para acceder a los permisos del plan de suscripción.
 * Simplifica el acceso a user?.cuenta?.permissions?.campo
 */
export function usePermissions() {
  const { currentUser: user } = useAuth();
  const permissions = user?.cuenta?.permissions || {};
  const cuenta = user?.cuenta || {};
  const planSlug = cuenta?.plan?.slug || '';

  // Superadmins globales siempre tienen acceso a todo
  const isSuperuser = user?.is_superuser === true;

  // Subscription status check
  const subscriptionStatus = cuenta.subscription_status || 'trial';
  // For PAID features (inventory, finance, etc.)
  const isSubscriptionValid = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
  // For GENERAL APP NAVIGATION (Dashboard, Patients list in read mode, etc.)
  // Inactive users CAN navigate the app, just with usage limits enforced by the backend
  const canNavigateApp = isSuperuser || subscriptionStatus === 'active' || subscriptionStatus === 'trial' || subscriptionStatus === 'inactive';
  
  // Plan-based access - orden-digital is basic plan without inventory/finance
  const isBasicPlan = planSlug === 'orden-digital';

  return {
    // Feature flags - based on plan (superusers bypass)
    canAccessInventory: isSuperuser || (isSubscriptionValid && !isBasicPlan),
    canAccessFinance: isSuperuser || (isSubscriptionValid && (permissions.can_access_financial_dashboard ?? !isBasicPlan)),
    canAccessAdvancedInventory: isSuperuser || (permissions.can_access_inventory_advanced ?? false),
    hasPrioritySupport: permissions.has_priority_support ?? false,
    
    // Limits
    maxUsersLimit: permissions.max_users_limit || 1,
    whatsappLimit: permissions.whatsapp_limit || 0,
    
    // Helper function for user limit check
    canAddMoreUsers: (currentUsersCount) => {
      const limit = permissions.max_users_limit || 1;
      return currentUsersCount < limit;
    },
    
    // Plan info
    planName: cuenta.plan_nombre || cuenta?.plan?.nombre || 'Sin Plan',
    planSlug: planSlug,
    isBasicPlan: isBasicPlan,
    subscriptionStatus: subscriptionStatus,
    isSubscriptionValid: isSubscriptionValid,
    canNavigateApp: canNavigateApp,
    isTrial: subscriptionStatus === 'trial',
    isActive: subscriptionStatus === 'active',
    isInactive: subscriptionStatus === 'inactive',
    isPastDue: subscriptionStatus === 'past_due',
    
    // Raw permissions object (for advanced use)
    rawPermissions: permissions,
  };
}

export default usePermissions;
